import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Flex, Row } from '@bb/elements-web';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getOrgsByGroup } from 'api/AnsweringOrgController';
import type { IGetOrgsByGroupResponse } from 'api/AnsweringOrgController/getOrgsByGroup';
import type { GroupData, GroupPreferences } from '../types';
import GroupPreferencesTree from './GroupPreferencesTree';

const NO_GROUP: GroupData = {
  id: 'noGroup',
  _id: 'noGroup',
  label: 'Non-Grouped Clients',
  orgs: [],
  owner: '',
  createdAt: '',
  updatedAt: '',
};

/**
 * Convert flat antd Tree checked-keys back into the nested GroupPreferences
 * structure expected by the API.
 */
const checkedKeysToGroupPreferences = (
  checkedKeys: string[],
  groups: GroupData[],
  existingPreferences: GroupPreferences,
): GroupPreferences => {
  const checkedSet = new Set(checkedKeys);
  // Preserve preferences for groups not yet loaded (pagination)
  const result: GroupPreferences = { ...existingPreferences };

  groups.forEach(group => {
    const groupId = group.id;
    const groupEntry: Record<string, boolean[]> = {};

    (group.orgs ?? []).forEach(org => {
      const locationBooleans: boolean[] = org.locations.map((loc, locIdx) => {
        if (loc.isDeleted) return false;
        const lKey = `${groupId}__${org.id}__${locIdx}`;
        return checkedSet.has(lKey);
      });

      groupEntry[org.id] = locationBooleans;
    });

    result[groupId] = groupEntry;
  });

  return result;
};

interface GroupPreferencesTabProps {
  groupPreferences: GroupPreferences;
  onGroupPreferencesChange: (groupPreferences: GroupPreferences) => void;
}

const GroupPreferencesTab: React.FC<GroupPreferencesTabProps> = ({
  groupPreferences,
  onGroupPreferencesChange,
}) => {
  const LIMIT = 20;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['orgsByGroup'],
      queryFn: ({ pageParam = 0 }) =>
        getOrgsByGroup(String(pageParam), String(LIMIT)),
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.hasMore) return undefined; // signals no more pages
        // skip = total items fetched so far
        return allPages.length * LIMIT;
      },
    });

  const groupData: GroupData[] = useMemo(() => {
    if (!data?.pages?.length) return [];
    const result = data.pages.reduce(
      (acc, page) =>
        acc.concat(
          page.groups.map(group => ({
            ...group,
            // eslint-disable-next-line no-underscore-dangle
            id: group._id,
            orgs: (group?.orgs ?? []).filter(org =>
              org.locations?.some(loc => !loc.isDeleted),
            ),
          })),
        ),
      [] as GroupData[],
    );

    const notGroupedOrgs: IGetOrgsByGroupResponse['notGroupedOrgs'] =
      data.pages.reduce((acc, page) => acc.concat(page.notGroupedOrgs), []);

    if (notGroupedOrgs.length) {
      result.push({
        ...NO_GROUP,
        orgs: notGroupedOrgs.filter(org =>
          org.locations?.some(loc => !loc.isDeleted),
        ),
      });
    }

    return result;
  }, [data?.pages]);

  const allExpandableKeys = useMemo(() => {
    const keys: string[] = [];
    groupData.forEach(group => {
      keys.push(group.id);
      (group.orgs ?? []).forEach(org => {
        keys.push(`${group.id}__${org.id}`);
      });
    });
    return keys;
  }, [groupData]);

  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  // Default to expanded when data loads
  useEffect(() => {
    if (allExpandableKeys.length > 0) {
      setExpandedKeys(allExpandableKeys);
    }
  }, [allExpandableKeys]);

  const onExpandAll = useCallback(() => {
    setExpandedKeys(allExpandableKeys);
  }, [allExpandableKeys]);

  const onCollapseAll = useCallback(() => {
    setExpandedKeys([]);
  }, []);

  const handleOnCheck = (checkedKeys: string[]) => {
    const updated = checkedKeysToGroupPreferences(
      checkedKeys,
      groupData,
      groupPreferences,
    );
    onGroupPreferencesChange(updated);
  };

  return (
    <Row>
      <Col span={24}>
        <Flex align="center" justify="flex-end" gap="8px">
          <Button
            dataCy="expand-all-groups-button"
            type="secondary"
            color="default"
            size="small"
            minWidth={0}
            onClick={onExpandAll}
          >
            Expand All
          </Button>
          <Button
            dataCy="collapse-all-groups-button"
            type="secondary"
            color="default"
            size="small"
            minWidth={0}
            onClick={onCollapseAll}
          >
            Collapse All
          </Button>
        </Flex>
      </Col>
      <Col span={24}>
        <GroupPreferencesTree
          groups={groupData}
          groupPreferences={groupPreferences}
          loadingOrgs={isFetchingNextPage}
          hasMore={hasNextPage}
          expandedKeys={expandedKeys}
          onExpand={setExpandedKeys}
          onCheck={handleOnCheck}
          onLoadMore={fetchNextPage}
        />
      </Col>
    </Row>
  );
};

export default GroupPreferencesTab;
